export const EQUALS_TRANSFER_ERROR_MESSAGES_BY_CODE = {
  // Validation errors
  MISSING_FIELDS: {
    en: 'Missing required fields: {fields}',
    fr: 'Champs obligatoires manquants : {fields}',
  },
  INVALID_TRANSFER_TYPE: {
    en: "Transfer type must be 'beneficiary', 'invoice' or 'internal_transfert'",
    fr: "Le type de transfert doit être 'beneficiary', 'invoice' ou 'internal_transfert'",
  },
  INVALID_AMOUNT: {
    en: 'Amount must be a positive number',
    fr: 'Le montant doit être un nombre positif',
  },
  INVALID_OTP_FORMAT: {
    en: 'Code must be a 6-digit number',
    fr: 'Le code doit être un nombre à 6 chiffres',
  },
  INVALID_DATE: {
    en: 'Settlement date must be in YYYYMMDD format',
    fr: 'La date de règlement doit être au format AAAAMMJJ',
  },

  // Business errors
  COUNTERPARTY_NOT_FOUND: {
    en: 'Counterparty not found',
    fr: 'Contrepartie introuvable',
  },
  IBAN_NOT_FOUND: {
    en: 'Counterparty IBAN not found',
    fr: 'IBAN de la contrepartie introuvable',
  },
  INVOICE_NOT_FOUND: {
    en: 'Invoice not found',
    fr: 'Facture introuvable',
  },
  ACCOUNT_COUNTERPARTY_NOT_FOUND: {
    en: 'No account counterparty found for this invoice currency',
    fr: 'Aucun compte contrepartie trouvé pour la devise de cette facture',
  },
  SOURCE_ACCOUNT_NOT_FOUND: {
    en: 'Source account not found',
    fr: 'Compte source introuvable',
  },
  TARGET_ACCOUNT_NOT_FOUND: {
    en: 'Target account not found',
    fr: 'Compte cible introuvable',
  },
  CURRENCY_MISMATCH: {
    en: 'Currency does not match the beneficiary account currency',
    fr: 'La devise ne correspond pas à la devise du compte bénéficiaire',
  },
  SAME_CURRENCY: {
    en: 'Source and target currencies are the same',
    fr: 'Les devises source et cible sont identiques',
  },
  MISSING_IBAN: {
    en: 'No IBAN associated with this counterparty',
    fr: 'Aucun IBAN associé à cette contrepartie',
  },
  EQUALS_RECIPIENT_NOT_CONFIGURED: {
    en: 'This counterparty is not linked to an Equals recipient',
    fr: "Cette contrepartie n'est pas liée à un destinataire Equals",
  },
  QUOTE_CREATION_FAILED: {
    en: 'Failed to create quote',
    fr: 'Échec de la création du devis de change',
  },
  INVALID_TRANSFER_SESSION: {
    en: 'Invalid transfer session',
    fr: 'Session de transfert invalide',
  },
  CACHE_NOT_FOUND: {
    en: 'Transfer session not found or expired',
    fr: 'Session de transfert introuvable ou expirée',
  },

  // OTP + profile/phone errors
  OTP_NOT_FOUND: {
    en: 'OTP not found',
    fr: 'Code OTP introuvable',
  },
  OTP_ALREADY_USED: {
    en: 'Code has already been used',
    fr: 'Le code a déjà été utilisé',
  },
  OTP_MAX_ATTEMPTS: {
    en: 'Maximum attempts exceeded',
    fr: 'Nombre maximum de tentatives dépassé',
  },
  OTP_EXPIRED: {
    en: 'Code has expired',
    fr: 'Le code a expiré',
  },
  OTP_INVALID_CODE: {
    en: 'Invalid code. Remaining attempts: {n}',
    fr: 'Code invalide. Tentatives restantes : {n}',
  },
  OTP_RATE_LIMIT_EXCEEDED: {
    en: 'Too many OTP requests. Please try again later.',
    fr: 'Trop de demandes de code. Veuillez réessayer plus tard.',
  },
  PROFILE_NOT_FOUND: {
    en: 'No profile found for this user',
    fr: 'Aucun profil trouvé pour cet utilisateur',
  },
  MISSING_PHONE: {
    en: 'No phone number associated with this account',
    fr: 'Aucun numéro de téléphone associé à ce compte',
  },
  INVALID_PHONE_FORMAT: {
    en: 'Invalid phone number format',
    fr: 'Format de numéro de téléphone invalide',
  },
};

function normalizeLocale(locale) {
  return locale === 'en' ? 'en' : 'fr';
}

function interpolate(template, params) {
  if (!template || !params) return template;
  return template.replace(/\{(\w+)\}/g, (m, k) => {
    const v = params[k];
    if (v === undefined || v === null) return m;
    if (Array.isArray(v)) return v.join(', ');
    return String(v);
  });
}

function parseRemainingAttemptsFromMessage(error) {
  const msg =
    error?.message || error?.msg || error?.error_message || error?.errorMessage;
  if (!msg) return undefined;

  const en = String(msg).match(/remaining attempts\s*:\s*(\d+)/i);
  if (en?.[1]) return Number(en[1]);

  const fr = String(msg).match(/tentatives restantes\s*:\s*(\d+)/i);
  if (fr?.[1]) return Number(fr[1]);

  return undefined;
}

function extractParams(error) {
  const ctx = error?.error_context || error?.context || {};

  const fields =
    error?.fields ??
    ctx?.fields ??
    ctx?.missing_fields ??
    ctx?.missingFields ??
    undefined;

  const n =
    error?.n ??
    error?.remaining_attempts ??
    error?.remainingAttempts ??
    ctx?.n ??
    ctx?.remaining_attempts ??
    ctx?.remainingAttempts ??
    parseRemainingAttemptsFromMessage(error) ??
    undefined;

  return {fields, n};
}

export function isEqualsTransferApi(api) {
  if (typeof api !== 'string') return false;
  return api === 'equals/quote' || api.startsWith('equals/transfer/');
}

export function resolveEqualsTransferErrorMessage({api, error, locale} = {}) {
  const loc = normalizeLocale(locale);
  if (!isEqualsTransferApi(api)) return null;

  const code = error?.error_code || error?.code || error?.errorCode;
  const fallbackMessage =
    error?.message || error?.error_message || error?.msg || null;
  if (!code) return `Une erreur est survenue : ${fallbackMessage}`;

  const msg = EQUALS_TRANSFER_ERROR_MESSAGES_BY_CODE?.[code]?.[loc];
  if (!msg) return fallbackMessage;

  return interpolate(msg, extractParams(error));
}
